import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { useId, useState } from "react";
import { useAnalytics } from "../../../lib/analytics";
import { useClientTranslations } from "../../../lib/i18n/client";
import type { OnboardingAnswers } from "../../../types/onboarding";
import { CycleDatePicker } from "./CycleDatePicker";
import styles from "./CycleQuestion.module.css";

interface CycleQuestionProps {
	answers: OnboardingAnswers;
	onInputChange: (field: string, value: string) => void;
}

interface StepperProps {
	label: string;
	hint?: string;
	value: number;
	min: number;
	max: number;
	field: string;
	disabled: boolean;
	onInputChange: (field: string, value: string) => void;
	t: (key: string) => string;
	infoText: string;
	infoOpen: boolean;
	onToggleInfo: () => void;
}

function Stepper({
	label,
	hint,
	value,
	min,
	max,
	field,
	disabled,
	onInputChange,
	t,
	infoText,
	infoOpen,
	onToggleInfo,
}: StepperProps) {
	const { logOnboarding } = useAnalytics();
	const setValue = (next: number, direction: "increment" | "decrement") => {
		const clamped = Math.min(max, Math.max(min, next));
		onInputChange(field, String(clamped));
		// Track the interaction + direction only — not the value, which is
		// reproductive-health data (lives in the DB, not analytics).
		logOnboarding("cycle_stepper_changed", { field, direction });
	};

	return (
		<div className={styles.field}>
			<div className={styles.labelRow}>
				<span className={styles.fieldLabel}>{label}</span>
				<button
					type="button"
					className={styles.infoButton}
					onClick={onToggleInfo}
					aria-label={`${label} – more information`}
				>
					<InformationCircleIcon className={styles.infoIcon} />
				</button>
			</div>
			{hint && <span className={styles.hint}>{hint}</span>}
			<div className={styles.stepper}>
				<button
					type="button"
					className={styles.stepperButton}
					aria-label={t("cycle.decrement")}
					disabled={disabled || value <= min}
					onClick={() => setValue(value - 1, "decrement")}
				>
					−
				</button>
				<span className={styles.stepperValue}>
					{value} {t("cycle.days")}
				</span>
				<button
					type="button"
					className={styles.stepperButton}
					aria-label={t("cycle.increment")}
					disabled={disabled || value >= max}
					onClick={() => setValue(value + 1, "increment")}
				>
					+
				</button>
			</div>
			{infoOpen && <div className={styles.infoTooltip}>{infoText}</div>}
		</div>
	);
}

// Controlled component: `notRelevant` is derived from props, not local state,
// so it stays in sync with the machine's answers on BACK/forward navigation.
// Steppers and toggle render display-only defaults (5 / 28 / Yes) when unset.
export function CycleQuestion({ answers, onInputChange }: CycleQuestionProps) {
	const { t } = useClientTranslations("onboarding");
	const { logOnboarding } = useAnalytics();
	const notRelevantId = useId();
	const notRelevant = answers.cycleNotRelevant === "true";

	// Per-field info tooltips (toggled by the ⓘ button), matching the profile
	// screens' pattern: tapping reveals a hidden info container.
	const [openInfo, setOpenInfo] = useState<Record<string, boolean>>({});
	const toggleInfo = (field: string) => {
		const open = !openInfo[field];
		setOpenInfo((prev) => ({ ...prev, [field]: open }));
		logOnboarding("cycle_info_toggled", { field, open });
	};

	const periodLength = Number(answers.cyclePeriodLengthDays ?? "5");
	const cycleLength = Number(answers.cycleLengthDays ?? "28");
	// No default selection for regularity: highlight a button only when the user
	// (or saved data) has an explicit value, so neither is pre-selected.
	const regularYes = answers.cycleRegular === "true";
	const regularNo = answers.cycleRegular === "false";

	return (
		<div className={styles.container}>
			<div
				className={`${styles.card} ${notRelevant ? styles.cardDisabled : ""}`}
			>
				<div className={styles.field}>
					<div className={styles.labelRow}>
						<span className={styles.fieldLabel}>
							{t("cycle.lastPeriodStart")}
						</span>
						<button
							type="button"
							className={styles.infoButton}
							onClick={() => toggleInfo("lastPeriodStart")}
							aria-label={`${t("cycle.lastPeriodStart")} – more information`}
						>
							<InformationCircleIcon className={styles.infoIcon} />
						</button>
					</div>
					<CycleDatePicker
						label={t("cycle.lastPeriodStart")}
						value={answers.cycleLastPeriodStart ?? ""}
						onChange={(v) => {
							onInputChange("cycleLastPeriodStart", v);
							logOnboarding("cycle_last_period_set", { has_value: v !== "" });
						}}
						placeholder={t("cycle.lastPeriodPlaceholder")}
						disabled={notRelevant}
						disabledDays={{ after: new Date() }}
						endMonth={new Date()}
					/>
					{openInfo.lastPeriodStart && (
						<div className={styles.infoTooltip}>
							{t("cycle.lastPeriodInfo")}
						</div>
					)}
				</div>

				<Stepper
					label={t("cycle.periodLength")}
					value={periodLength}
					min={1}
					max={14}
					field="cyclePeriodLengthDays"
					disabled={notRelevant}
					onInputChange={onInputChange}
					t={t}
					infoText={t("cycle.periodLengthInfo")}
					infoOpen={!!openInfo.periodLength}
					onToggleInfo={() => toggleInfo("periodLength")}
				/>

				<Stepper
					label={t("cycle.cycleLength")}
					hint={t("cycle.cycleLengthHint")}
					value={cycleLength}
					min={15}
					max={60}
					field="cycleLengthDays"
					disabled={notRelevant}
					onInputChange={onInputChange}
					t={t}
					infoText={t("cycle.cycleLengthInfo")}
					infoOpen={!!openInfo.cycleLength}
					onToggleInfo={() => toggleInfo("cycleLength")}
				/>

				<div className={styles.field}>
					<div className={styles.labelRow}>
						<span className={styles.fieldLabel}>{t("cycle.regular")}</span>
						<button
							type="button"
							className={styles.infoButton}
							onClick={() => toggleInfo("regular")}
							aria-label={`${t("cycle.regular")} – more information`}
						>
							<InformationCircleIcon className={styles.infoIcon} />
						</button>
					</div>
					<div className={styles.toggleGroup}>
						<button
							type="button"
							className={`${styles.toggleButton} ${
								regularYes ? styles.toggleButtonSelected : ""
							}`}
							aria-pressed={regularYes}
							disabled={notRelevant}
							onClick={() => {
								onInputChange("cycleRegular", "true");
								logOnboarding("cycle_regular_selected", { regular: true });
							}}
						>
							{t("cycle.regularYes")}
						</button>
						<button
							type="button"
							className={`${styles.toggleButton} ${
								regularNo ? styles.toggleButtonSelected : ""
							}`}
							aria-pressed={regularNo}
							disabled={notRelevant}
							onClick={() => {
								onInputChange("cycleRegular", "false");
								logOnboarding("cycle_regular_selected", { regular: false });
							}}
						>
							{t("cycle.regularNo")}
						</button>
					</div>
					{openInfo.regular && (
						<div className={styles.infoTooltip}>{t("cycle.regularInfo")}</div>
					)}
				</div>
			</div>

			<label className={styles.notRelevantRow} htmlFor={notRelevantId}>
				<input
					id={notRelevantId}
					type="checkbox"
					className={styles.visuallyHidden}
					aria-label={t("cycle.notRelevant")}
					checked={notRelevant}
					onChange={(e) => {
						onInputChange("cycleNotRelevant", String(e.target.checked));
						logOnboarding("cycle_not_relevant_toggled", {
							checked: e.target.checked,
						});
					}}
				/>
				<span
					className={`${styles.checkbox} ${
						notRelevant ? styles.checkboxChecked : ""
					}`}
				>
					{notRelevant && (
						<svg
							width="12"
							height="9"
							viewBox="0 0 12 9"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
							className={styles.checkmark}
							role="img"
							aria-label="Selected"
						>
							<path
								d="M1 4.5L4.5 8L11 1"
								stroke="white"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					)}
				</span>
				<span className={styles.notRelevantLabel}>
					{t("cycle.notRelevant")}
				</span>
			</label>
		</div>
	);
}
